import * as THREE from 'three';
import type {CircleIntersectionDefinition2D} from './CircleCircleIntersectionSolver';
import {
    TrigonometricEquationRootSolver,
    type TrigonometricEquationCoefficients,
} from './TrigonometricEquationRootSolver';

/**
 * 二维椭圆解析定义。
 */
export interface EllipseIntersectionDefinition2D {
    /** 椭圆中心。 */
    center: THREE.Vector2;
    /** 第一半轴长度。 */
    radiusX: number;
    /** 第二半轴长度。 */
    radiusY: number;
    /** 椭圆相对二维坐标系 X 轴的旋转角。 */
    rotation: number;
}

/**
 * 椭圆与圆的单个真实交点。
 */
export interface EllipseCircleIntersection2D {
    /** 二维交点坐标。 */
    point: THREE.Vector2;
    /** 交点在完整椭圆参数方程上的角参数，范围 `[0,2π)`。 */
    ellipseAngle: number;
    /** 交点在圆参数方程上的角参数，范围 `[0,2π)`。 */
    circleAngle: number;
}

/**
 * 椭圆与圆的二维真实交点求解器。
 *
 * 职责：
 * 1. 使用椭圆参数方程与圆隐式方程构造二次三角方程；
 * 2. 复用公共三角方程根求解器取得全部参数角；
 * 3. 回代真实圆方程并恢复双方参数；
 * 4. 对相切重根和周期接缝交点执行空间去重。
 */
export class EllipseCircleIntersectionSolver extends TrigonometricEquationRootSolver {
    /** 隐式方程残差相对容差。 */
    private static readonly IMPLICIT_RELATIVE_TOLERANCE = 1e-10;

    /** 世界交点去重相对容差。 */
    private static readonly POINT_RELATIVE_TOLERANCE = 1e-10;

    /**
     * 求解椭圆与圆的全部真实交点。
     * @param ellipse 二维椭圆定义。
     * @param circle 二维圆定义。
     * @returns 按椭圆角参数升序排列的真实交点。
     */
    public solve(
        ellipse: EllipseIntersectionDefinition2D,
        circle: CircleIntersectionDefinition2D,
    ): EllipseCircleIntersection2D[] {
        if (!this.hasValidDefinition(ellipse, circle)) {
            console.error('[EllipseCircleIntersectionSolver] 椭圆或圆定义无效，无法计算真实交点。', {
                ellipse,
                circle,
            });
            return [];
        }

        const cosineRotation = Math.cos(ellipse.rotation);
        const sineRotation = Math.sin(ellipse.rotation);
        const origin = ellipse.center.clone().sub(circle.center);
        const cosineBasis = new THREE.Vector2(
            ellipse.radiusX * cosineRotation,
            ellipse.radiusX * sineRotation,
        );
        const sineBasis = new THREE.Vector2(
            -ellipse.radiusY * sineRotation,
            ellipse.radiusY * cosineRotation,
        );
        const geometryScale = Math.max(
            ellipse.radiusX,
            ellipse.radiusY,
            circle.radius,
            origin.length(),
        );
        const inverseGeometryScale = 1 / geometryScale;
        const normalizedOrigin = origin.clone().multiplyScalar(inverseGeometryScale);
        const normalizedCosineBasis = cosineBasis.clone().multiplyScalar(inverseGeometryScale);
        const normalizedSineBasis = sineBasis.clone().multiplyScalar(inverseGeometryScale);
        const normalizedCircleRadius = circle.radius * inverseGeometryScale;

        // 在共同特征尺度下展开 `|origin + U cosθ + V sinθ|² - r² = 0`。
        const coefficients: TrigonometricEquationCoefficients = [
            normalizedCosineBasis.dot(normalizedCosineBasis),
            2 * normalizedCosineBasis.dot(normalizedSineBasis),
            normalizedSineBasis.dot(normalizedSineBasis),
            2 * normalizedOrigin.dot(normalizedCosineBasis),
            2 * normalizedOrigin.dot(normalizedSineBasis),
            normalizedOrigin.lengthSq() - normalizedCircleRadius * normalizedCircleRadius,
        ];
        const roots = this.solveTrigonometricEquation(coefficients);

        // 恒等方程表示椭圆退化为与圆完全重合的圆，不存在有限裁剪边界。
        if (roots.isIdentical) {
            return [];
        }

        const pointToleranceSquared = Math.max(
            geometryScale * EllipseCircleIntersectionSolver.POINT_RELATIVE_TOLERANCE,
            Number.EPSILON,
        ) ** 2;
        const circleResidualTolerance = geometryScale
            * geometryScale
            * EllipseCircleIntersectionSolver.IMPLICIT_RELATIVE_TOLERANCE;
        const intersections: EllipseCircleIntersection2D[] = [];

        roots.angles.forEach((ellipseAngle) => {
            const point = ellipse.center.clone()
                .addScaledVector(cosineBasis, Math.cos(ellipseAngle))
                .addScaledVector(sineBasis, Math.sin(ellipseAngle));
            const circleResidual = Math.abs(point.distanceToSquared(circle.center) - circle.radius * circle.radius);
            if (circleResidual > circleResidualTolerance) {
                return;
            }
            if (intersections.some((candidate) => candidate.point.distanceToSquared(point) <= pointToleranceSquared)) {
                return;
            }

            intersections.push({
                point,
                ellipseAngle,
                circleAngle: this.normalizeAngle(Math.atan2(
                    point.y - circle.center.y,
                    point.x - circle.center.x,
                )),
            });
        });

        return intersections;
    }

    /**
     * 判断输入定义是否可参与解析求交。
     * @param ellipse 椭圆定义。
     * @param circle 圆定义。
     * @returns 所有参数有效且半径为正时返回 true。
     */
    private hasValidDefinition(
        ellipse: EllipseIntersectionDefinition2D,
        circle: CircleIntersectionDefinition2D,
    ): boolean {
        return Number.isFinite(ellipse.center.x)
            && Number.isFinite(ellipse.center.y)
            && Number.isFinite(ellipse.radiusX)
            && ellipse.radiusX > 0
            && Number.isFinite(ellipse.radiusY)
            && ellipse.radiusY > 0
            && Number.isFinite(ellipse.rotation)
            && Number.isFinite(circle.center.x)
            && Number.isFinite(circle.center.y)
            && Number.isFinite(circle.radius)
            && circle.radius > 0;
    }
}
